import { currencyFormatter } from "@fla/common";
import BankIcon from "@fla/components/src/assets/svgs/bank_card.svg";
import SplitIcon from "@fla/components/src/assets/svgs/split.svg";
import { banks } from "@fla/components/src/definitions/bank";

import { EditIcon } from "../../../../SvgIcon";
import { Bank_InitialState } from "../../../../interfaces";
import { useNames } from "../useNames";

type Props = { bankData: Bank_InitialState[] } & ReturnType<typeof useNames>;

export function useBank({ bankData, spouseFirstName, userFirstName, getName }: Props) {
  const getBankDetails = (val: string) => {
    return (
      banks.find(data => data.value === val) || {
        label: val,
        value: val,
        img: BankIcon
      }
    );
  };

  const getValue = (data: any, vFor: string) => {
    if (data.split_by === "percentage") {
      return (Number(data.value) * Number(data.percentage[vFor])) / 100;
    } else {
      return Number(data.value) / 2;
    }
  };

  const totalAmount = (data: any, amountFor: string) => {
    if (data.split_by === amountFor) {
      return data.value;
    } else return getValue(data, amountFor);
  };

  const userTotalAmount = (() => {
    return bankData
      .filter(data => ["user", "percentage", "50_50"].includes(data.split_by))
      .reduce((p, curr) => p + Number(totalAmount(curr, "user")), 0);
  })();

  const spouseTotalAmount = (() => {
    return bankData
      .filter(data => ["spouse", "percentage", "50_50"].includes(data.split_by))
      .reduce((p, curr) => p + Number(totalAmount(curr, "spouse")), 0);
  })();

  /**
   *
   * @param {*} val "user" | "spouse"
   * @param {*} editData function
   * @returns
   */
  const ownCard = (val: "user" | "spouse", editData?: any) => {
    return Array.isArray(bankData) && bankData.length
      ? bankData
          .filter(data => data.split_by === val)
          .map((data, idx) => {
            const details = getBankDetails(data.name);
            return (
              <article key={idx} className="bg-white rounded-xl p-5 shadow-md mb-5">
                <section className="grid grid-cols-8 gap-y-4 md:gap-y-0 mb-2">
                  <div className="col-span-2 md:col-span-1">
                    <img src={details.img} className="w-12 h-auto my-3" alt="superannuation" />
                  </div>
                  <div className="col-span-6 md:col-span-5 space-y-1">
                    <p className="text-lg font-semibold">{details.label}</p>
                    <p className="text-sm">
                      Account holder:&nbsp;
                      <span className="font-medium">{getName(data.owner)}</span>
                    </p>

                    <div className="grid grid-cols-2 md:space-x-4">
                      <p className="text-sm col-span-2 md:col-span-1">
                        BSB:&nbsp;
                        <span className="font-medium">{data.bsb}</span>
                      </p>
                      <p className="text-sm col-span-2 md:col-span-1">
                        Account:&nbsp;
                        <span className="font-medium">{data.acc_number}</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-span-8 md:col-span-2 flex flex-col items-end">
                    <p className="text-xl">{currencyFormatter.format(Number(data.value))}</p>
                    <p className="text-gray-500 text-sm">Balance</p>
                    {typeof editData === "function" ? (
                      <button
                        className="transition-all hover:bg-primary/20 px-2 py-1 rounded-md text-sm text-primary font-bold uppercase mt-4 flex items-center space-x-2"
                        onClick={() => editData(data)}
                      >
                        <EditIcon classes="w-3.5 h-3.5" />
                        <p className="tracking-wide">Edit</p>
                      </button>
                    ) : null}
                  </div>
                </section>
                {data?.comment ? <p className="line-clamp-1 pl-11 text-sm">{data.comment}</p> : null}
              </article>
            );
          })
      : null;
  };

  /**
   *
   * @param {*} val "user" | "spouse"
   * @param {*} editData function
   * @returns
   */
  const splitCard = (val: "user" | "spouse", editData?: any) => {
    return Array.isArray(bankData) && bankData.length
      ? bankData
          .filter(data => ["50_50", "percentage"].includes(data.split_by))
          .map((data, idx) => {
            const details = getBankDetails(data.name);

            return (
              <article key={idx} className="bg-white rounded-xl p-5 shadow-md mb-5">
                <section className="grid grid-cols-8 gap-y-4 md:gap-y-0 mb-2">
                  <div className="col-span-2 md:col-span-1">
                    <img src={details.img} className="w-12 h-auto my-3" alt="superannuation" />
                  </div>
                  <div className="col-span-6 md:col-span-5 space-y-1">
                    <p className="text-lg font-semibold">{details.label}</p>
                    <p className="text-sm">
                      Account holder:&nbsp;
                      <span className="font-medium">{getName(data.owner)}</span>
                    </p>
                    <p className="text-sm">
                      Current Balance:&nbsp;
                      <span className="font-medium">{currencyFormatter.format(Number(data.value))}</span>
                    </p>
                    <div className="grid grid-cols-2 md:space-x-4">
                      <p className="text-sm col-span-2 md:col-span-1">
                        BSB:&nbsp;
                        <span className="font-medium">{data.bsb}</span>
                      </p>
                      <p className="text-sm col-span-2 md:col-span-1">
                        Account:&nbsp;
                        <span className="font-medium">{data.acc_number}</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-span-8 md:col-span-2 flex flex-col items-end">
                    <p className="text-xl">{currencyFormatter.format(getValue(data, val))}</p>
                    <p className="text-gray-500 text-sm">{getName(val)}'s Share</p>

                    {typeof editData === "function" ? (
                      <button className="text-sm text-mki-blue-100" onClick={() => editData(data)}>
                        Edit
                      </button>
                    ) : null}
                  </div>
                </section>
                {data?.comment ? <p className="line-clamp-1 pl-11 text-sm">{data.comment}</p> : null}
                <hr />
                <section className="flex justify-between items-center mt-4">
                  <div className="px-3 py-1 rounded-md bg-gray-300 flex items-center">
                    <img src={SplitIcon} className="w-4 h-auto mr-2" alt="split" />
                    <p className="text-sm">To Split</p>
                  </div>
                  {data.split_by === "50_50" ? (
                    <div className="text-sm text-gray-500">
                      <p>
                        50% To {userFirstName} ({currencyFormatter.format(Number(data.value) / 2)})
                      </p>
                      <p>
                        50% To {spouseFirstName} ({currencyFormatter.format(Number(data.value) / 2)})
                      </p>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      <p>
                        {data.percentage.user}% To {userFirstName} (
                        {currencyFormatter.format((Number(data.value) * Number(data.percentage.user)) / 100)})
                      </p>
                      <p>
                        {data.percentage.spouse}% To {spouseFirstName} (
                        {currencyFormatter.format((Number(data.value) * Number(data.percentage.spouse)) / 100)})
                      </p>
                    </div>
                  )}
                </section>
              </article>
            );
          })
      : null;
  };
  return { ownCard, splitCard, userTotalAmount, spouseTotalAmount };
}
